/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import {
    IBuyboxProductConfigureDropdownViewProps,
    IBuyboxProductConfigureViewProps,
    IBuyboxShopSimilarLookViewProps
} from '@msdyn365-commerce-modules/buybox';
import { IBuyboxData, IBuyboxViewProps } from '@msdyn365-commerce-modules/buybox';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as Msdyn365 from '@msdyn365-commerce/core';
import MsDyn365 from '@msdyn365-commerce/core';
import { AttributeValue, SimpleProduct, DeliveryOption, ProjectionDomain, ProductPrice } from '@msdyn365-commerce/retail-proxy';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import get from 'lodash/get';

// Types
import { IBuyboxProps, IBuyboxResources } from '../definition-extensions/buybox.ext.props.autogenerated';
import { IBuyboxData as IBuyboxViewData } from './buybox.data';
import { IFullOrgUnitAvailability } from '../actions/utilities/full-org-unit-availability';

// Actions and utilities
import { getFallbackImageUrl } from '@msdyn365-commerce-modules/retail-actions';
import { CartModulesStateShareInput } from '../../../actions/cart-state-share';
import { IDobbiesAddedProductToBundle } from '../../../modules/dobbies-product-collection/dobbies-product-collection';
import { _getCartItemMaxQuantity } from '../../../modules/dobbies-cart/components/dobbies-cart-line-items';
import { getAllOrgUnitLocationsByArea, GetAllOrgUnitLocationsByAreaInput } from '../../../actions/get-all-store-location-information';
import { vSIInitiateRestCallAsync } from '../../../dotdigital-extension/BackInStockDataAction.g';
import { CheckboxType, ICheckboxState } from '../../../modules/dobbies-cookie-compliance/dobbies-cookie-compliance';

// Utilities
import { DeliveryBoxesType, getAddToCartBoxes, isLowStock } from '../utilities/del-modes-util';
import { _getScreenSize } from '../utilities/display-utils';
import { getOrgUnitLocObjSelected, setSelectedStore } from '../utilities/cnc-utils';
import { checkIsOverMaxInventoryQuantity } from '../utilities/is-over-max-quantity';

// Components
import getBuyboxAddToCart from './custom-components/get-add-to-cart';
import { getBuyboxFindInStore, IBuyboxFindInStoreViewProps } from './custom-components/get-buybox-find-in-store';
import RenderBanners from './custom-components/image-banners';
import { VsiCustomBadges } from './custom-components/vsi-custom-badges';
import BackInStockModal from './custom-components/back-in-stock-modal';
import ClubPlusBadge from './custom-components/club-plus-badge';
import StickyProductHeader from './custom-components/sticky-product-header';
import DeliveryMethodSection from './custom-components/delivery-method-section';
import ProductBundleSection from './custom-components/product-bundle-section';
import AddToCartContainer from './custom-components/add-to-cart-container';
import PlantaBlock from './custom-components/planta-block';
import MobileProductHeader from './custom-components/mobile-product-header';
import { getActivePricesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';

export interface IDobbiesMiniCartSummaryProduct extends SimpleProduct {
    quantity?: number;
}

// ==========================================
// Main BuyboxView Component
// ==========================================
const BuyboxView: React.FC<IBuyboxViewProps & IBuyboxProps<IBuyboxData & IBuyboxViewData>> = props => {
    const {
        ModuleProps,
        ProductInfoContainerProps,
        addToWishlist,
        configure,
        title,
        rating,
        shopSimilarLook,
        context: {
            actionContext: {
                requestContext: { cookies }
            }
        }
    } = props;

    const {
        data: { ratingsSummary, productAttributes, product },
        config: { selectedAttributes },
        quantity,
        callbacks,
        state,
        resources,
        slots: { productDetails, ageRestrictionNotification }
    } = props;

    // ==========================================
    // State
    // ==========================================
    const [addToCartTiles, setAddToCartTiles] = useState<(JSX.Element[] | undefined)[]>();
    const [loggedInZipCode, setLoggedinZipCode] = useState('');
    const [isHomeDelTileSelected, setIsHomeDelTileSelected] = useState(false);
    const [isCNCTileSelected, setIsCNCTileSelected] = useState(false);
    const [isHomeDelLowStock, setIsHomeDelLowStock] = useState(false);
    const [isCNCLowStock, setIsCNCLowStock] = useState(false);
    const [selectedCNCOrgUnitLoc, setSelectedCNCOrgUnitLoc] = useState<IFullOrgUnitAvailability | undefined>();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [addToCartModalOpen, setAddToCartModalOpen] = useState(false);
    const [screenSize, setScreenSize] = useState(0);
    const [attributes, setAttributes] = useState<AttributeValue[]>([]);
    const [isVendorShipped, setIsVendorShipped] = useState(true);
    const [isAgeRestricted, setIsAgeRestricted] = useState(false);
    const [productAddedToCart, setProductAddedToCart] = useState(false);
    const [isProductAvailable, setIsProductAvailable] = useState<boolean | undefined>(true);
    const [isValidEmail, setIsValidEmail] = useState(true);
    const [platform, setPlatform] = useState('');
    const [customerEmail, setCustomerEmail] = useState('');
    const [privacyPolicyChecked, setPrivacyPolicyChecked] = useState(false);
    const [isCnCProductAvailable, setIsCnCProductAvailable] = useState<boolean | undefined>(false);
    const [showBackInStockModal, setShowBackInStockModal] = useState(false);
    const [showThankyoutext, setShowThankyoutext] = useState(false);
    const [displayClubPlusInfo, setDisplayClubPlusInfo] = useState(false);
    const [cartProducts, setCartProducts] = useState<IDobbiesMiniCartSummaryProduct[]>([]);
    const [renderProductBundlesModal, setRenderProductBundlesModal] = useState(false);
    const [bundlesSummaryScrollPos, setBundlesSummaryScrollPos] = useState(0);
    const [bundlesModalSummaryScrollPos, setBundlesModalSummaryScrollPos] = useState(0);
    const [allBundleProductsOOS, setAllBundleProductsOOS] = useState(false);
    const [isMaxQuantityReached, setIsMaxQuantityReached] = useState(false);
    const [isHomeDeliveryOptionAvailable, setIsHomeDeliveryOptionAvailable] = useState(false);
    const [isComingSoonProduct, setIsComingSoonProduct] = useState(false);
    const [isSticky, setIsSticky] = useState(false);
    const [productPrices, setProductPrices] = useState<ProductPrice[]>();

    // Refs
    const mediaGalleryRef = useRef<HTMLDivElement>(null);
    const stickyRef = useRef<HTMLDivElement | null>(null);
    const isModalClosedWithoutSelection = useRef<boolean>(false);

    // ==========================================
    // Derived values
    // ==========================================
    const allAttributes = productAttributes.result;
    const defaultMinimumKeyInPrice = 10;
    const defaultMaximumKeyInPrice = 100;
    const navigationUrl = Msdyn365.getUrlSync('cart', props.context.actionContext);
    const productDeliveryOptions = props.data.productDeliveryOptions.result?.deliveryOptions;
    const reviewsCount = ratingsSummary?.result?.reviewsCount;
    const productAvailabilitesForCNC = props.data.productCncAvailabilities.result?.availabilities;
    const isEcomProductAvailable = props.data?.productAvailableQuantity?.result?.some(item => item.IsProductAvailable) ?? false;

    const onlyCNCAvailable =
        productAttributes.result?.find(attr => attr.Name?.toLocaleLowerCase() === 'onlyinstore')?.TextValue?.toLowerCase() === 'yes';

    const activePrices = productPrices && productPrices.length > 0 ? productPrices[0].CustomerContextualPrice : undefined;
    const discountLines = productPrices && productPrices.length > 0 ? productPrices[0].DiscountLines : undefined;

    const checkUserDiscounts = discountLines?.find(x => x.OfferId === props.context.app.config.discountId);
    const isQuickView = !!props.context.request.query?.isQuickView;

    // Product attribute checks
    const isISO =
        productAttributes.result?.find(attr => attr.Name?.toLocaleLowerCase() === 'onlyinstore')?.TextValue?.toLowerCase() === 'yes';
    const outOfSeason =
        productAttributes.result?.find(attr => attr.Name?.toLocaleLowerCase() === 'outofseason')?.TextValue?.toLowerCase() === 'yes';
    const outOfSeasonCheck = outOfSeason && !isProductAvailable;
    const comingSoonCategoryName: string | undefined = props.config.comingSoonCategoryName;
    const comingSoonAttr =
        productAttributes.result
            ?.find(attr => attr.Name?.toLowerCase() === comingSoonCategoryName?.toLowerCase())
            ?.TextValue?.toLowerCase() === 'yes';

    // Restricted postcodes
    const restrictedPostcodeAttr = productAttributes.result?.find(pa => pa.Name === 'RestrictedPostCode');
    const restrictedPostcodeTextVal = restrictedPostcodeAttr?.TextValue;
    let postcodesArr: string[] = [];
    if (restrictedPostcodeTextVal) {
        try {
            postcodesArr = JSON.parse(restrictedPostcodeTextVal).RestrictedPostCode;
        } catch {
            postcodesArr = [];
        }
    }

    // Image settings
    const baseImageUrl: string = props.context.request.apiSettings.baseImageUrl;
    const fallbackImageUrl = product && getFallbackImageUrl(product.result?.ItemId, props.context.actionContext.requestContext.apiSettings);
    const imageSettings = {
        viewports: {
            xs: { q: 'w=148&h=208&m=6', w: 0, h: 0 },
            sm: { q: 'w=148&h=208&m=6', w: 0, h: 0 },
            lg: { q: 'w=148&h=208&m=6', w: 0, h: 0 }
        },
        lazyload: true
    };

    // Carousel heading for YMAL
    const carouselHeading = get(props, 'slots.unbxdAlsoLikeCarousel[0].props.config.carouselHeading');
    const carouselHeadingHref = carouselHeading
        ?.toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-');

    // Bundle products
    const bundleProducts = get(props.slots.bundledProductsSlot?.[0], 'props.config.productCollection.products') || [];
    const bundleHasProducts = bundleProducts.length > 0;
    const bundleProductsModal = get(props.slots.bundledProductsModalSlot?.[0], 'props.config.productCollection.products') || [];
    const bundleModalHasProducts = bundleProductsModal.length > 0;

    // Device check
    const isMobiles = screenSize === 1;

    // YMAL visibility
    let hideYmlUnbxd: boolean | undefined = false;
    if (props.config?.checkStockForYmlUnbxd) {
        hideYmlUnbxd = !isEcomProductAvailable && !isCnCProductAvailable;
    }

    // ==========================================
    // Utility functions
    // ==========================================
    const validateEmail = (email: string) => {
        const regex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return regex.test(String(email).toLowerCase());
    };

    const getTierId = useCallback(() => {
        if (props.data.loyaltyCard.result?.LoyaltyGroups?.length && props.data.loyaltyCard.result?.LoyaltyGroups?.length > 0) {
            const highestActiveLoyaltyCardTier = props.data.loyaltyCard.result!.LoyaltyGroups![0]?.HighestActiveLoyaltyCardTier;
            const loyaltyCardTiers = props.data.loyaltyCard.result!.LoyaltyGroups![0]?.LoyaltyCardTiers;
            const loyaltyCardTier = loyaltyCardTiers?.find(tier => tier.RecordId === highestActiveLoyaltyCardTier);
            return loyaltyCardTier?.LoyaltyTierRecordId;
        }
        return undefined;
    }, [props.data.loyaltyCard.result]);

    const getVendorName = useCallback(() => {
        const vendornameAttr = props.data.productAttributes.result?.find(attribute => {
            const attributeName = attribute.Name?.trim().toLowerCase();
            return attributeName === 'vendorname';
        });
        return vendornameAttr?.TextValue || undefined;
    }, [props.data.productAttributes.result]);

    const renderProductTitle = useCallback((): string => {
        // @ts-ignore
        let prodTitle = title?.props?.text;
        const attrEcom = allAttributes?.find(s => s.Name === 'EComName');
        if (attrEcom?.TextValue) {
            prodTitle = attrEcom.TextValue;
        }
        return prodTitle;
    }, [title, allAttributes]);

    const renderClubPlusBadges = useCallback((): string | undefined => {
        return activePrices?.toFixed(2);
    }, [activePrices]);

    // ==========================================
    // Cookie helpers
    // ==========================================
    const removeSelectedCncCookie = useCallback(() => {
        const selectedStoresCookie = props.context.actionContext.requestContext.cookies.get('selected_cnc_stores');
        if (selectedStoresCookie.value) {
            props.context.actionContext.requestContext.cookies.remove('selected_cnc_stores');
        }
    }, [props.context.actionContext.requestContext.cookies]);

    const removeBundleProductsCookie = useCallback(() => {
        const actionCookies = props.context.actionContext.requestContext.cookies;
        actionCookies.remove('added_products_bundles');
        actionCookies.remove('pdp_product_quantity_for_bundles');
        setCartProducts([]);
    }, [props.context.actionContext.requestContext.cookies]);

    const isVirtualCartEmpty = useCallback((): boolean => {
        const actionCookies = props.context.actionContext.requestContext.cookies;
        const productsAddedInBundles = actionCookies.get('added_products_bundles')?.value || [];
        return Array.isArray(productsAddedInBundles) ? !productsAddedInBundles.length : false;
    }, [props.context.actionContext.requestContext.cookies]);

    // ==========================================
    // Event handlers
    // ==========================================
    const handleNotifyMeClick = useCallback(() => {
        setPrivacyPolicyChecked(false);
        setCustomerEmail(props.data.customerInformation.result?.Email ?? '');
        setIsValidEmail(true);
        setShowBackInStockModal(true);
        setShowThankyoutext(false);
    }, [props.data.customerInformation.result?.Email]);

    const onChangeEmail = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setCustomerEmail(event.target.value);
    }, []);

    const onChangePrivacy = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setPrivacyPolicyChecked(event.target.checked);
    }, []);

    const displayClubPlus = useCallback(() => {
        setDisplayClubPlusInfo(prev => !prev);
    }, []);

    const closeProductBundlesModal = useCallback(() => {
        setRenderProductBundlesModal(false);
    }, []);

    const clearCncSelection = useCallback(() => {
        removeSelectedCncCookie();
        setIsCNCTileSelected(false);
        setSelectedCNCOrgUnitLoc(undefined);
        setIsHomeDelTileSelected(true);
    }, [removeSelectedCncCookie]);

    // ==========================================
    // Cart and CnC handlers
    // ==========================================
    const onAddToCartClicked = useCallback(() => {
        if (isCNCTileSelected && product.result) {
            props.data.cart.result
                ?.addProductToCart({
                    product: product.result,
                    count: state.quantity,
                    location: selectedCNCOrgUnitLoc?.OrgUnitAvailability?.OrgUnitLocation,
                    deliveryMode: 'ClickCol'
                })
                .then(() => {
                    const behavior = props.context.app.config?.addToCartBehavior;
                    if (behavior === 'showModal') {
                        callbacks.updateErrorState({ configureErrors: {} });
                        setAddToCartModalOpen(true);
                    }
                });
        }
    }, [
        isCNCTileSelected,
        product.result,
        state.quantity,
        selectedCNCOrgUnitLoc,
        props.data.cart.result,
        props.context.app.config,
        callbacks
    ]);

    const onLocChanged = useCallback(() => {
        if (product.result?.RecordId) {
            getOrgUnitLocObjSelected(product.result.RecordId, props.context.actionContext)
                .then(res => {
                    setSelectedCNCOrgUnitLoc(res);
                    setIsCNCTileSelected(true);
                })
                .catch(() => {});
        }
    }, [product.result?.RecordId, props.context.actionContext]);

    const updateModeOfAddToCart = useCallback(
        (isHomeDelSelected: boolean, isCNCSelected: boolean) => {
            setIsHomeDelTileSelected(isHomeDelSelected);
            setIsCNCTileSelected(isCNCSelected);

            // When switching to Home Delivery, clear the store from state (but keep the cookie)
            if (!isCNCSelected) {
                setSelectedCNCOrgUnitLoc(undefined);
                return;
            }

            // Reset the modal closed flag when user explicitly selects CnC
            isModalClosedWithoutSelection.current = false;

            // Only fetch and set the store when CnC is selected
            if (product.result?.RecordId) {
                getOrgUnitLocObjSelected(product.result.RecordId, props.context.actionContext)
                    .then((res: IFullOrgUnitAvailability | undefined) => {
                        // Only set store if modal wasn't closed without selection
                        if (!isModalClosedWithoutSelection.current) {
                            setSelectedCNCOrgUnitLoc(res);
                        }
                    })
                    .catch(() => {});
            }
        },
        [product.result?.RecordId, props.context.actionContext]
    );

    // ==========================================
    // Stock check functions
    // ==========================================
    const checkIsCncAvailable = useCallback(() => {
        setIsLoading(true);
        const productId: number = props.data.product.result?.RecordId!;

        const isCNCStockAvailable = productAvailabilitesForCNC?.some(availability => availability.IsProductAvailable);
        const cncDeliveryOption =
            productDeliveryOptions?.length &&
            productDeliveryOptions?.length > 0 &&
            productDeliveryOptions[0].DeliveryOptions?.find((option: any) => option.Code === 'ClickCol');

        if (isCNCStockAvailable && cncDeliveryOption) {
            setIsCnCProductAvailable(true);

            if (
                isLowStock(
                    productId,
                    productAvailabilitesForCNC || [],
                    props.context.actionContext.requestContext.app.config.warehouseIdsForFulfillment.map((x: string) => x.toLowerCase()),
                    false
                )
            ) {
                setIsCNCLowStock(true);
            }
        }
        setIsLoading(false);
    }, [
        productAvailabilitesForCNC,
        productDeliveryOptions,
        props.data.product.result?.RecordId,
        props.context.actionContext.requestContext.app.config.warehouseIdsForFulfillment
    ]);

    const getProductPrices = useCallback(async () => {
        const ctx = props.context;
        const priceAffiliations = [
            {
                AffiliationId: ctx.app.config.AffiliationId,
                LoyaltyTierId: ctx.app.config.LoyaltyTierId,
                AffiliationTypeValue: ctx.app.config.AffiliationTypeValue,
                ReasonCodeLines: [],
                CustomerId: '',
                ExtensionProperties: []
            }
        ];

        const productRecId: number = product.result?.RecordId || 0;
        const projectDomain: ProjectionDomain = {
            ChannelId: ctx.request.apiSettings.channelId,
            CatalogId: ctx.request.apiSettings.catalogId
        };
        const prices = await getActivePricesAsync(
            { callerContext: ctx.actionContext, queryResultSettings: {} },
            projectDomain,
            [productRecId],
            new Date(),
            null,
            priceAffiliations,
            true
        );

        setProductPrices(prices);
    }, [props.data.product.result?.RecordId, props.context.actionContext]);

    const checkIsComingSoon = useCallback(() => {
        if (comingSoonCategoryName) {
            const attr = productAttributes.result?.find(attr => attr.Name?.toLowerCase() === comingSoonCategoryName.toLowerCase());
            if (attr?.TextValue?.toLowerCase() === 'yes') {
                setIsComingSoonProduct(true);
            }
        }
    }, [comingSoonCategoryName, productAttributes.result]);

    // ==========================================
    // Back in stock submission
    // ==========================================
    const showThankyouNote = useCallback(async () => {
        const mail = customerEmail || props.context.request.user.emailAddress || '';
        if (!validateEmail(mail)) {
            setIsValidEmail(false);
        } else {
            setIsValidEmail(true);
            const itemID = product.result?.ItemId;
            const recID = product.result?.RecordId;

            if (!itemID || !recID) return;
            try {
                await vSIInitiateRestCallAsync(
                    { callerContext: props.context.actionContext },
                    props.context.request.user.customerAccountNumber ?? '',
                    mail,
                    itemID,
                    props.context.app?.config?.popularityBannerCompany ?? 'DGC',
                    '1',
                    false,
                    recID
                );
            } catch (e) {
                console.log(e);
            } finally {
                setShowThankyoutext(true);
            }
        }
    }, [customerEmail, product.result, props.context]);

    // ==========================================
    // Initialize findInStore and addToCart
    // ==========================================
    const checkUserLoyalty = getTierId() === props.context.app.config.LoyaltyTierId ? true : undefined;
    const findInStore = getBuyboxFindInStore(props, state, callbacks, resources, onAddToCartClicked, onLocChanged);
    const addToCart = getBuyboxAddToCart(
        props,
        state,
        callbacks,
        resources,
        isAgeRestricted,
        defaultMinimumKeyInPrice,
        defaultMaximumKeyInPrice,
        onAddToCartClicked,
        addToCartModalOpen,
        setAddToCartModalOpen,
        isCNCTileSelected,
        isHomeDelTileSelected,
        isCnCProductAvailable,
        findInStore,
        isMaxQuantityReached
    );

    // ==========================================
    // Scroll handler for sticky header
    // ==========================================
    const handleScroll = useCallback(() => {
        const trigger = document.querySelector('.ms-buybox__content .price-quantity');
        const footer = document.querySelector('footer');
        if (!trigger || !footer) return;

        const triggerRect = trigger.getBoundingClientRect();
        const footerRect = footer.getBoundingClientRect();
        const hasPassedTrigger = triggerRect.bottom <= 0;
        const footerInView = footerRect.top <= window.innerHeight;

        setIsSticky(hasPassedTrigger && !footerInView);
    }, []);

    // ==========================================
    // Platform detection
    // ==========================================
    const getPlatform = useCallback(() => {
        if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Windows Phone/i.test(navigator.userAgent)) {
            if (navigator.userAgent.match(/Android/i)) {
                setPlatform('Android');
            } else if (navigator.userAgent.match(/iPhone|iPad|iPod/i)) {
                setPlatform('IOS');
            }
        } else {
            setPlatform('Web');
        }
    }, []);

    // ==========================================
    // Cookie setup
    // ==========================================
    const setCookies = useCallback(() => {
        const _cookieExpirationInSeconds: number = 5_184_000;
        let newRecentlyViewed: number[] | undefined = [];
        const cookieConfig = { maxAge: _cookieExpirationInSeconds };

        const recentlyViewedProdIdsCookie: any = cookies.get('recently-viewed');
        let recentlyViewedArr = recentlyViewedProdIdsCookie.value;

        if (recentlyViewedArr) {
            if (recentlyViewedArr.includes(props.data.product.result?.RecordId)) {
                recentlyViewedArr = recentlyViewedArr.filter((x: number | undefined) => x !== props.data.product.result?.RecordId);
            }
            if (recentlyViewedArr.length >= 10) {
                recentlyViewedArr = recentlyViewedArr.slice(0, recentlyViewedArr.length - 1);
            }
            newRecentlyViewed = [props.data.product.result?.RecordId, ...recentlyViewedArr];
        } else {
            if (props.data.product.result?.RecordId) {
                newRecentlyViewed = [props.data.product.result?.RecordId];
            }
        }

        cookies.set('recently-viewed', newRecentlyViewed, cookieConfig);
    }, [cookies, props.data.product.result?.RecordId]);

    const setCookieAndPlatformState = useCallback(() => {
        const cookieValue = cookies.get<ICheckboxState[]>('cookieConsent').value;
        const analyticsCookies = cookieValue?.find(cookie => cookie.name === CheckboxType.Analytics);
        if (analyticsCookies?.value) {
            setCookies();
        }
        getPlatform();
        mediaGalleryRef.current?.addEventListener('contextmenu', e => e.preventDefault());
    }, [cookies, setCookies, getPlatform]);

    // ==========================================
    // Quantity helpers
    // ==========================================
    const getOrderQuantityLimitsFeatureFlag = useCallback((): boolean => {
        const defaultOrderQuantityLimitsFeatureConfig = props.context?.request?.app?.platform?.enableDefaultOrderQuantityLimits;
        if (defaultOrderQuantityLimitsFeatureConfig === 'none') return false;

        try {
            const featureStatuses = props.data.featureState.result;
            const isQuantityLimitsFeatureEnabledInHq =
                featureStatuses?.find(
                    featureState => featureState.Name === 'Dynamics.AX.Application.RetailDefaultOrderQuantityLimitsFeature'
                )?.IsEnabled || false;
            if (!isQuantityLimitsFeatureEnabledInHq) return false;
        } catch {
            return false;
        }

        if (defaultOrderQuantityLimitsFeatureConfig === 'all') return true;

        try {
            const customerInfo = props.data.customerInformation.result;
            return (
                !!customerInfo &&
                ((defaultOrderQuantityLimitsFeatureConfig === 'b2b' && customerInfo.IsB2b) ||
                    (defaultOrderQuantityLimitsFeatureConfig === 'b2c' && !customerInfo.IsB2b))
            );
        } catch {
            return false;
        }
    }, [props.context, props.data.featureState.result, props.data.customerInformation.result]);

    const updateStateQuantity = useCallback(() => {
        const cncInventory = productAvailabilitesForCNC?.find(
            inventory => inventory.InventLocationId === selectedCNCOrgUnitLoc?.OrgUnitAvailability?.OrgUnitLocation?.InventoryLocationId
        );
        let productInventory = props.data.productAvailableQuantity.result?.find(
            inventory => inventory.ProductAvailableQuantity.ProductId === product.result?.RecordId
        );
        if (isCNCTileSelected && cncInventory) {
            productInventory = cncInventory;
        }

        let isOverMaxQuantity = false;
        let maxQuantity = 1;
        if (
            productInventory?.ProductAvailableQuantity.AvailableQuantity &&
            productInventory.ProductAvailableQuantity.AvailableQuantity < props.context.app.config.maxQuantityForCartLineItem
        ) {
            isOverMaxQuantity = state.quantity > productInventory.ProductAvailableQuantity.AvailableQuantity;
            maxQuantity = productInventory.ProductAvailableQuantity.AvailableQuantity;
        } else {
            isOverMaxQuantity = state.quantity > props.context.app.config.maxQuantityForCartLineItem;
            maxQuantity = props.context.app.config.maxQuantityForCartLineItem;
        }

        if (isOverMaxQuantity) {
            callbacks.updateQuantity(maxQuantity);
        }
    }, [
        productAvailabilitesForCNC,
        selectedCNCOrgUnitLoc,
        props.data.productAvailableQuantity.result,
        product.result?.RecordId,
        isCNCTileSelected,
        state.quantity,
        props.context.app.config.maxQuantityForCartLineItem,
        callbacks
    ]);

    const setProductAvailabilityCookieForBundles = useCallback(
        (qAvailable: number) => {
            const actionCookies = props.context.actionContext.requestContext.cookies;
            actionCookies.set('pdp_product_quantity_for_bundles', qAvailable);
        },
        [props.context.actionContext.requestContext.cookies]
    );

    // ==========================================
    // Bundle functions
    // ==========================================
    const isMaximumQuantityAdded = useCallback((): boolean => {
        const actionCookies = props.context.actionContext.requestContext.cookies;
        const productsAddedInBundles = actionCookies.get('added_products_bundles')?.value || [];
        const productList = new Array();
        const simpleProducts: SimpleProduct[] = cartProducts.filter(cp => {
            if (Array.isArray(productsAddedInBundles)) {
                return productsAddedInBundles.find(addedp => addedp['productId'] === cp.RecordId);
            }
            return false;
        });

        simpleProducts.forEach(p => {
            let quantity = 1;
            if (Array.isArray(productsAddedInBundles)) {
                const ap = productsAddedInBundles.find(addedp => addedp['productId'] === p.RecordId);
                quantity = ap['quantity'];
                const prodInCart = props.data.cart.result?.cart.CartLines?.find(cl => cl.ProductId === ap['productId']);
                if (prodInCart?.Quantity && quantity + prodInCart.Quantity > ap['maxQuantity']) {
                    quantity = -1;
                }
            }
            productList.push({ product: p, count: quantity, enableStockCheck: props.context.app.config.enableStockCheck });
        });

        return productList.some(sp => sp.count === -1);
    }, [
        cartProducts,
        props.context.actionContext.requestContext.cookies,
        props.data.cart.result?.cart.CartLines,
        props.context.app.config.enableStockCheck
    ]);

    // ==========================================
    // useEffects
    // ==========================================

    // Initial setup
    useEffect(() => {
        if (props.context.request.user.isAuthenticated && props.data.customerInformation.result?.Email) {
            setCustomerEmail(props.data.customerInformation.result.Email);
        }
        window.addEventListener('scroll', handleScroll);
        getProductPrices();
        removeSelectedCncCookie();
        removeBundleProductsCookie();
        setCookieAndPlatformState();
        checkIsCncAvailable();
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Delivery options check
    useEffect(() => {
        if (productDeliveryOptions?.length && productDeliveryOptions?.length > 0) {
            const deliveryOpt = productDeliveryOptions.find(option => option.ProductId === product.result?.RecordId);
            if (deliveryOpt) {
                const homeDeliveryOption = deliveryOpt.DeliveryOptions?.find(
                    (option: DeliveryOption) => option.Code !== 'ClickCol' && option.Code !== 'Common MOD'
                );
                setIsHomeDeliveryOptionAvailable(!!homeDeliveryOption);
            }
        }
    }, [productDeliveryOptions, product.result?.RecordId]);

    // Bundle products OOS state
    useEffect(() => {
        if (props.data.bundleProductsShareState.result?.areAllBundleProductsOOS === true && !allBundleProductsOOS) {
            setAllBundleProductsOOS(true);
        } else if (props.data.bundleProductsShareState.result?.areAllBundleProductsOOS === false && allBundleProductsOOS) {
            setAllBundleProductsOOS(false);
        }
    }, [props.data.bundleProductsShareState.result, allBundleProductsOOS]);

    // CnC tile selection
    useEffect(() => {
        if (isCNCTileSelected && findInStore?.clickCNC && !selectedCNCOrgUnitLoc) {
            findInStore.clickCNC();
        }
    }, [isCNCTileSelected, findInStore, selectedCNCOrgUnitLoc]);

    // Modal close handling
    useEffect(() => {
        if (!props.data.clickCollectModalState.result?.isModalOpen && !selectedCNCOrgUnitLoc && isCNCTileSelected) {
            // Set flag to prevent async callbacks from re-opening the modal
            isModalClosedWithoutSelection.current = true;
            setIsCNCTileSelected(false);
        }
    }, [props.data.clickCollectModalState.result?.isModalOpen, selectedCNCOrgUnitLoc, isCNCTileSelected]);

    // Coming soon check
    useEffect(() => {
        checkIsComingSoon();
    }, [checkIsComingSoon]);

    // Max quantity check
    useEffect(() => {
        let inventory = props.data.productAvailableQuantity.result?.find(
            item => item.ProductAvailableQuantity.ProductId === product.result?.RecordId
        );
        let isCNCItem = false;
        if (isCNCTileSelected && selectedCNCOrgUnitLoc) {
            const cncStoreStock = productAvailabilitesForCNC?.find(
                inv => inv.InventLocationId === selectedCNCOrgUnitLoc.OrgUnitAvailability?.OrgUnitLocation?.InventoryLocationId
            );
            inventory = cncStoreStock;
            isCNCItem = true;
        }
        const maxReached = checkIsOverMaxInventoryQuantity(
            state.quantity,
            props.data.cart.result?.cart.CartLines || [],
            product.result?.RecordId || 0,
            isCNCItem,
            inventory,
            selectedCNCOrgUnitLoc?.OrgUnitAvailability?.OrgUnitLocation?.OrgUnitNumber
        );
        setIsMaxQuantityReached(maxReached);
    }, [
        state.quantity,
        props.data.cart.result?.cart.CartLines,
        isHomeDelTileSelected,
        isCNCTileSelected,
        selectedCNCOrgUnitLoc,
        productAvailabilitesForCNC,
        props.data.productAvailableQuantity.result,
        product.result?.RecordId
    ]);

    // Default delivery option selection
    useEffect(() => {
        if (props.context.request.user.isAuthenticated && isCnCProductAvailable && (onlyCNCAvailable || !isEcomProductAvailable)) {
            const customerLocalStore = props.data.customerInformation.result?.Attributes?.find(attr => attr.Name === 'Local Store');

            _getAllStores(props, 31.5070767, 74.4315609, 0, 0).then(allStores => {
                const preferStore = allStores.find(
                    store => store.OrgUnitAvailability?.OrgUnitLocation?.OrgUnitName === customerLocalStore?.AttributeValue?.StringValue
                );
                const preferStoreStock = productAvailabilitesForCNC?.find(
                    inv => inv.InventLocationId === preferStore?.OrgUnitAvailability?.OrgUnitLocation?.InventoryLocationId
                );
                if (preferStoreStock?.IsProductAvailable) {
                    setSelectedStore(
                        preferStore?.OrgUnitLocation?.InventoryLocationId || '',
                        product.result?.RecordId || 0,
                        props.context.actionContext
                    );
                    setSelectedCNCOrgUnitLoc(preferStore);
                    setIsCNCTileSelected(true);
                }
            });
        } else if (isEcomProductAvailable && isHomeDeliveryOptionAvailable) {
            setIsHomeDelTileSelected(true);
        }
    }, [
        isCnCProductAvailable,
        isEcomProductAvailable,
        isHomeDeliveryOptionAvailable,
        productAvailabilitesForCNC,
        onlyCNCAvailable,
        props.context.request.user.isAuthenticated,
        props.data.customerInformation.result,
        product.result?.RecordId,
        props.context.actionContext
    ]);

    // Product availability and attributes
    useEffect(() => {
        if (attributes.length < 1 && allAttributes) {
            _getCustomAttribute(
                allAttributes,
                selectedAttributes,
                attributes,
                setAttributes,
                setIsAgeRestricted,
                setIsVendorShipped
            ).catch(() => {});
        }
        if ((!productAddedToCart && findInStore?.isAddedToCart) || addToCart?.isAddedToCart) {
            setProductAddedToCart(true);
        }

        const isAvailable = props.data.productAvailableQuantity.result?.find(availability => availability.IsProductAvailable === true);
        const qAvailable = props.data.productAvailableQuantity.result?.find(
            info => info.ProductAvailableQuantity.ProductId === product.result?.RecordId && info.ProductAvailableQuantity.AvailableQuantity
        );
        const maxQuantity = _getCartItemMaxQuantity(
            props.context.app.config.maxQuantityForCartLineItem,
            props.context.app.config.enableStockCheck || false,
            qAvailable?.ProductAvailableQuantity.AvailableQuantity || 0,
            getOrderQuantityLimitsFeatureFlag(),
            product?.result?.Behavior?.MaximumQuantity || 0
        );
        setProductAvailabilityCookieForBundles(maxQuantity || 0);
        setIsProductAvailable(isAvailable?.IsProductAvailable ?? false);
    }, [
        productAddedToCart,
        findInStore,
        addToCart,
        allAttributes,
        selectedAttributes,
        attributes,
        props.data.productAvailableQuantity.result,
        product.result,
        props.context.app.config,
        getOrderQuantityLimitsFeatureFlag,
        setProductAvailabilityCookieForBundles
    ]);

    // Add to cart tiles updates
    useEffect(() => {
        if (product.result?.RecordId) {
            const hasCncOption = props.data.deliveryOptions.result?.DeliveryOptions?.some(opt => opt.Code === 'ClickCol') || false;
            const hasHomeDelOption = isHomeDeliveryOptionAvailable && !isISO;
            const dataLoaded = !isLoading && props.data.productDeliveryOptions.result?.deliveryOptions !== undefined;
            const tiles = getAddToCartBoxes({
                type: DeliveryBoxesType.PDP,
                isHomeDelSelected: isHomeDelTileSelected,
                isCNCSelected: isCNCTileSelected,
                hasHomeDelOption: hasHomeDelOption,
                hasCncOption: hasCncOption,
                hasHomeDelStock: isProductAvailable || false,
                hasCncStock: isCnCProductAvailable || false,
                actionContext: props.context.actionContext,
                updateModeOfAddToCart,
                deliveryOptions: props.data.productDeliveryOptions.result?.deliveryOptions,
                productAttributes: productAttributes.result,
                isHomeDelLowStock,
                isCNCLowStock,
                isLoading: !dataLoaded
            });
            setAddToCartTiles(tiles);
        }
    }, [
        isCnCProductAvailable,
        isHomeDelTileSelected,
        isCNCTileSelected,
        isHomeDelLowStock,
        isCNCLowStock,
        product.result?.RecordId,
        isISO,
        isProductAvailable,
        props.data.deliveryOptions.result,
        props.context.actionContext,
        updateModeOfAddToCart,
        props.data.productDeliveryOptions.result,
        productAttributes.result,
        isLoading,
        isHomeDeliveryOptionAvailable
    ]);

    // Quantity state update
    useEffect(() => {
        updateStateQuantity();
    }, [isHomeDelTileSelected, isCNCTileSelected, selectedCNCOrgUnitLoc, updateStateQuantity]);

    // Screen size handling
    useEffect(() => {
        if (!MsDyn365.isBrowser) return;
        setScreenSize(_getScreenSize(props.context));
        const handleResize = () => setScreenSize(_getScreenSize(props.context));
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [props.context]);

    // Low stock check
    if (props.data && Array.isArray(props.data.productAvailableQuantity.result)) {
        for (const inventory of props.data.productAvailableQuantity.result) {
            if (inventory.StockLevelLabel === 'LOW') {
                if (!isHomeDelLowStock) setIsHomeDelLowStock(true);
                break;
            }
        }
    }

    // Initial tiles setup - render immediately with loading state to prevent layout shift
    if (!addToCartTiles && product.result?.RecordId) {
        const hasCncOption = props.data.deliveryOptions.result?.DeliveryOptions?.some(opt => opt.Code === 'ClickCol') || false;
        const hasHomeDelOption = isHomeDeliveryOptionAvailable && !isISO;
        const dataLoaded = !isLoading && props.data.productDeliveryOptions.result?.deliveryOptions !== undefined;
        const tiles = getAddToCartBoxes({
            type: DeliveryBoxesType.PDP,
            isHomeDelSelected: isHomeDelTileSelected,
            isCNCSelected: isCNCTileSelected,
            hasHomeDelOption: hasHomeDelOption,
            hasCncOption: hasCncOption,
            hasHomeDelStock: isProductAvailable || false,
            hasCncStock: isCnCProductAvailable || false,
            actionContext: props.context.actionContext,
            updateModeOfAddToCart,
            deliveryOptions: props.data.productDeliveryOptions.result?.deliveryOptions,
            productAttributes: productAttributes.result,
            isHomeDelLowStock,
            isCNCLowStock,
            isLoading: !dataLoaded
        });
        setAddToCartTiles(tiles);

        if (props.context.actionContext.requestContext.user.isAuthenticated) {
            const addresses = props.data.customerInformation?.result?.Addresses;
            if (addresses?.length) {
                const customerAddress = addresses.find(addr => addr.IsPrimary) || addresses[0];
                const addressPostcode = customerAddress?.ZipCode;
                if (addressPostcode && loggedInZipCode !== addressPostcode) {
                    setLoggedinZipCode(addressPostcode);
                }
            }
        }
    }

    // ==========================================
    // Computed values for Add to Cart Container
    // ==========================================
    const deliveryOptions = props.data.deliveryOptions?.result?.DeliveryOptions;
    const isCnC = deliveryOptions?.some(opt => opt.Code === 'ClickCol');

    const cncInventory = productAvailabilitesForCNC?.find(
        inventory => inventory.InventLocationId === selectedCNCOrgUnitLoc?.OrgUnitAvailability?.OrgUnitLocation?.InventoryLocationId
    );
    let productInventory = props.data.productAvailableQuantity.result?.find(
        inventory => inventory.ProductAvailableQuantity.ProductId === product.result?.RecordId
    );
    if (isCNCTileSelected && cncInventory) {
        productInventory = cncInventory;
    }

    let isQuantityReached = false;
    if (
        productInventory?.ProductAvailableQuantity.AvailableQuantity &&
        productInventory.ProductAvailableQuantity.AvailableQuantity < props.context.app.config.maxQuantityForCartLineItem
    ) {
        isQuantityReached = state.quantity >= productInventory.ProductAvailableQuantity.AvailableQuantity;
    } else {
        isQuantityReached = state.quantity >= props.context.app.config.maxQuantityForCartLineItem;
    }

    // ==========================================
    // Render
    // ==========================================
    return (
        <>
            <Module {...ModuleProps}>
                {/* Sticky Header */}
                {(isCnCProductAvailable || isEcomProductAvailable) && (
                    <StickyProductHeader
                        isSticky={isSticky}
                        stickyRef={stickyRef}
                        baseImageUrl={baseImageUrl}
                        productImageUrl={product.result?.PrimaryImageUrl}
                        fallbackImageUrl={fallbackImageUrl}
                        productName={product.result?.Name}
                        productTitle={renderProductTitle()}
                        priceComponent={props.price}
                        gridSettings={props.context.request.gridSettings!}
                        imageSettings={imageSettings}
                        isISO={isISO}
                        onlyCNCAvailable={onlyCNCAvailable}
                        isEcomProductAvailable={isEcomProductAvailable}
                        isCnCProductAvailable={isCnCProductAvailable}
                        isHomeDelTileSelected={isHomeDelTileSelected}
                        isCNCTileSelected={isCNCTileSelected}
                        onSelectHomeDelivery={clearCncSelection}
                        onSelectCnC={() => {
                            setIsCNCTileSelected(true);
                            setIsHomeDelTileSelected(false);
                        }}
                        addToCartContainer={
                            <AddToCartContainer
                                isComingSoonProduct={isComingSoonProduct}
                                isEcomProductAvailable={isEcomProductAvailable}
                                isCnCProductAvailable={isCnCProductAvailable}
                                isProductAvailable={isProductAvailable}
                                isISO={isISO}
                                outOfSeasonCheck={outOfSeasonCheck}
                                isHomeDelTileSelected={isHomeDelTileSelected}
                                isCNCTileSelected={isCNCTileSelected}
                                isCnC={isCnC || false}
                                isVendorShipped={isVendorShipped}
                                isQuantityReached={isQuantityReached}
                                isMaxQuantityReached={isMaxQuantityReached}
                                quantityError={props.state.errorState.quantityError}
                                isAuthenticated={props.context.actionContext.requestContext.user.isAuthenticated}
                                quantityComponent={quantity}
                                addToCart={addToCart}
                                addToWishlist={addToWishlist}
                                findInStore={findInStore}
                                callbacks={callbacks}
                                state={state}
                                resources={resources}
                                selectedCNCOrgUnitLoc={selectedCNCOrgUnitLoc}
                                productAvailabilitesForCNC={productAvailabilitesForCNC}
                                maxQuantityForCartLineItem={props.context.app.config.maxQuantityForCartLineItem}
                                productData={product.result}
                                productAvailableQuantity={props.data.productAvailableQuantity.result}
                                onNotifyMeClick={handleNotifyMeClick}
                            />
                        }
                        addedToCartModal={addToCart?.addedToCartModal}
                        carouselHeading={carouselHeading}
                        carouselHeadingHref={carouselHeadingHref}
                        hideYmlUnbxd={hideYmlUnbxd}
                        unbxdRecommendationsCount={props.data.getUnbxdRecommendationsCount?.result?.count ?? 0}
                    />
                )}

                {/* Mobile Header */}
                {isMobiles && (
                    <MobileProductHeader
                        productData={{ ...props.data.product.result, AttributeValues: props.data.productAttributes.result }}
                        productTitle={renderProductTitle()}
                        context={props.context}
                        isLoading={isLoading}
                        isComingSoonProduct={isComingSoonProduct}
                        isProductAvailableCNC={isCnCProductAvailable}
                        isProductAvailableEcom={isEcomProductAvailable}
                        isISO={isISO}
                        priceComponent={props.price}
                        displayClubPlusInfo={displayClubPlusInfo}
                        onToggleClubPlus={displayClubPlus}
                        priceTooltipText={props.config.priceTooltipText}
                        clubPlusPrice={renderClubPlusBadges()}
                        checkUserLoyalty={checkUserLoyalty}
                        checkUserDiscounts={!!checkUserDiscounts}
                    />
                )}

                {/* Find In Store Modal */}
                {!outOfSeasonCheck && findInStore && _renderFindInStore(findInStore)}

                {/* Media Gallery */}
                <div ref={mediaGalleryRef} className='ms-buybox__media-gallery'>
                    {props.mediaGallery}
                </div>

                {/* Product Info Container */}
                <Node {...ProductInfoContainerProps}>
                    {/* Desktop Header */}
                    {!isMobiles && (
                        <>
                            {!isLoading && (
                                <VsiCustomBadges
                                    productData={{ ...props.data.product.result, AttributeValues: props.data.productAttributes.result }}
                                    context={props.context}
                                    isProductAvailableCNC={isCnCProductAvailable}
                                    isProductAvailableEcom={isEcomProductAvailable}
                                    isISO={isISO}
                                    isComingSoon={isComingSoonProduct}
                                    mode='text'
                                />
                            )}
                            <Node className='ms-buybox__product-title' tag='h1'>
                                {renderProductTitle()}
                            </Node>

                            <div className='product-badge-pdp-container'>
                                {props.price}
                                <ClubPlusBadge
                                    displayClubPlusInfo={displayClubPlusInfo}
                                    onToggleClubPlus={displayClubPlus}
                                    priceTooltipText={props.config.priceTooltipText}
                                    clubPlusPrice={renderClubPlusBadges()}
                                    checkUserLoyalty={checkUserLoyalty}
                                    checkUserDiscounts={!!checkUserDiscounts}
                                />
                            </div>
                        </>
                    )}

                    {/* Rating */}
                    {!props.context.app.config.hideRating && (
                        <Node tag='div' className='rating-holder'>
                            {rating}
                            {_renderReviewCount(reviewsCount, resources)}
                        </Node>
                    )}

                    {/* Configure */}
                    {configure && _renderConfigure(configure)}

                    {/* Quick Links */}
                    <div className='links'>
                        <a href='#key-features' onClick={e => _handleLinkClick(e, 'key-features')}>
                            Key features
                        </a>
                        <a href='#delivery-returns' onClick={e => _handleLinkClick(e, 'delivery-returns')}>
                            Delivery &amp; returns
                        </a>
                    </div>

                    {/* Delivery Method Section */}
                    {!isComingSoonProduct && (isLoading || isISO || isEcomProductAvailable || isCnCProductAvailable) && (
                        <DeliveryMethodSection
                            isISO={isISO}
                            onlyCNCAvailable={onlyCNCAvailable}
                            isEcomProductAvailable={isEcomProductAvailable}
                            isCnCProductAvailable={isCnCProductAvailable}
                            isProductAvailable={isProductAvailable}
                            isComingSoonProduct={isComingSoonProduct}
                            addToCartTiles={addToCartTiles}
                            isHomeDelTileSelected={isHomeDelTileSelected}
                            isCNCTileSelected={isCNCTileSelected}
                            selectedCNCOrgUnitLoc={selectedCNCOrgUnitLoc}
                            storeNamePrefix={props.config.storeNamePrefix}
                            findInStore={findInStore}
                            vendorName={getVendorName()}
                            loggedInZipCode={loggedInZipCode}
                            restrictedPostcodes={postcodesArr}
                            actionContext={props.context.actionContext}
                            deliveryOptions={props.data.productDeliveryOptions.result?.deliveryOptions[0]?.DeliveryOptions}
                            cartLineLearnMoreLink={props.context.actionContext.requestContext.app.config.cartLineLearnMoreLink}
                            onNotifyMeClick={handleNotifyMeClick}
                            isLoading={isLoading}
                        />
                    )}

                    {/* Age Restriction Notification */}
                    {isAgeRestricted && _renderTextBlock(ageRestrictionNotification)}

                    {/* Add To Cart Container */}
                    <AddToCartContainer
                        isComingSoonProduct={isComingSoonProduct}
                        isEcomProductAvailable={isEcomProductAvailable}
                        isCnCProductAvailable={isCnCProductAvailable}
                        isProductAvailable={isProductAvailable}
                        isISO={isISO}
                        outOfSeasonCheck={outOfSeasonCheck}
                        isLoading={isLoading}
                        isHomeDelTileSelected={isHomeDelTileSelected}
                        isCNCTileSelected={isCNCTileSelected}
                        isCnC={isCnC || false}
                        isVendorShipped={isVendorShipped}
                        isQuantityReached={isQuantityReached}
                        isMaxQuantityReached={isMaxQuantityReached}
                        quantityError={props.state.errorState.quantityError}
                        isAuthenticated={props.context.actionContext.requestContext.user.isAuthenticated}
                        quantityComponent={quantity}
                        addToCart={addToCart}
                        addToWishlist={addToWishlist}
                        findInStore={findInStore}
                        callbacks={callbacks}
                        state={state}
                        resources={resources}
                        selectedCNCOrgUnitLoc={selectedCNCOrgUnitLoc}
                        productAvailabilitesForCNC={productAvailabilitesForCNC}
                        maxQuantityForCartLineItem={props.context.app.config.maxQuantityForCartLineItem}
                        productData={product.result}
                        productAvailableQuantity={props.data.productAvailableQuantity.result}
                        onNotifyMeClick={handleNotifyMeClick}
                    />

                    {/* Out of stock YMAL */}
                    {hideYmlUnbxd &&
                        props.slots.unbxdAlsoLikeCarousel &&
                        !comingSoonAttr &&
                        props.slots.unbxdAlsoLikeCarousel.length > 0 && (
                            <div className='unbxd-recommendations outofstock'>{props.slots.unbxdAlsoLikeCarousel}</div>
                        )}

                    {/* Back In Stock Modal */}
                    <BackInStockModal
                        isOpen={showBackInStockModal}
                        onClose={() => setShowBackInStockModal(false)}
                        showThankyoutext={showThankyoutext}
                        isAuthenticated={props.context.request.user.isAuthenticated}
                        customerEmail={customerEmail}
                        onChangeEmail={onChangeEmail}
                        isValidEmail={isValidEmail}
                        emailPlaceholderText={props.resources.emailAddressPlaceholderText}
                        privacyPolicyChecked={privacyPolicyChecked}
                        onChangePrivacy={onChangePrivacy}
                        privacyPolicyUrl={Msdyn365.getUrlSync('privacyPolicy', props.context.actionContext) || ''}
                        onSubmit={showThankyouNote}
                    />

                    {/* Added to cart notification */}
                    {productAddedToCart && _renderAddedToCartNotification(resources, navigationUrl)}

                    {/* Banners */}
                    {(isEcomProductAvailable || isCnCProductAvailable) && <RenderBanners {...props} />}

                    {/* Text blocks */}
                    {_renderTextBlock(props.slots.textBlocks)}

                    {/* Additional features */}
                    {!isQuickView && (
                        <>
                            {false && <PlantaBlock platform={platform} {...props.config} />}
                            {_renderSocialShare(props.slots?.socialShare)}
                            {shopSimilarLook && _renderShopSimilarItem(shopSimilarLook)}
                        </>
                    )}
                </Node>
            </Module>

            {/* Product Details Slot */}
            {productDetails}

            {/* Product Bundle Section */}
            <ProductBundleSection
                allBundleProductsOOS={allBundleProductsOOS}
                bundleHasProducts={bundleHasProducts}
                bundleModalHasProducts={bundleModalHasProducts}
                showProductBundlesInModal={props.context.app.config.showProductBundlesInModal}
                isVirtualCartEmpty={isVirtualCartEmpty()}
                isMaximumQuantityAdded={isMaximumQuantityAdded()}
                renderProductBundlesModal={renderProductBundlesModal}
                onCloseModal={closeProductBundlesModal}
                bundledProductsSlot={props.slots.bundledProductsSlot}
                bundledProductsModalSlot={props.slots.bundledProductsModalSlot}
                cartLines={props.data.cart.result?.cart.CartLines || []}
                cartProducts={cartProducts}
                productBundlesOrderSummaryText={props.config.productBundlesOrderSummaryText}
                discountAppliedInCartMessage={props.config.discountAppliedInCartMessage}
                maxQuantityReachedInBundlesmsg={props.config.maxQuantityReachedInBundlesmsg}
                totalPriceText={props.config.totalPriceText}
                onAddAllProductsToCart={() => _addAllProductsToCart(props, cartProducts)}
                onQuantityChange={(productId, newValue, maxQuantity) => {
                    _onQuantityChange(props, productId, newValue, cartProducts, setCartProducts, false, maxQuantity);
                }}
                onRemoveProduct={(productId, isSummaryInModal) => {
                    _onRemoveProduct(props, productId, isSummaryInModal);
                }}
                context={props.context}
                bundlesSummaryScrollPos={bundlesSummaryScrollPos}
                setBundlesSummaryScrollPos={setBundlesSummaryScrollPos}
                bundlesModalSummaryScrollPos={bundlesModalSummaryScrollPos}
                setBundlesModalSummaryScrollPos={setBundlesModalSummaryScrollPos}
                productsAddedInBundles={
                    (props.context.actionContext.requestContext.cookies.get('added_products_bundles')?.value as any[]) || []
                }
            />

            {/* YMAL Carousel */}
            {!hideYmlUnbxd && props.slots.unbxdAlsoLikeCarousel && props.slots.unbxdAlsoLikeCarousel.length > 0 && (
                <div id={carouselHeadingHref} className='unbxd-recommendations' style={{ scrollMarginTop: '50px' }}>
                    {props.slots.unbxdAlsoLikeCarousel}
                </div>
            )}
        </>
    );
};

// ==========================================
// Helper functions
// ==========================================

const _handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
        el.dispatchEvent(new CustomEvent('openAccordion', { bubbles: true }));
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
};

const _getAllStores = async (
    props: IBuyboxProps<IBuyboxData & IBuyboxViewData>,
    latitude?: number,
    longitude?: number,
    radius?: number,
    distanceUnitValue?: number
): Promise<IFullOrgUnitAvailability[]> => {
    const orgUnitLocationsInput = new GetAllOrgUnitLocationsByAreaInput(latitude, longitude, radius, distanceUnitValue, false);
    return getAllOrgUnitLocationsByArea(orgUnitLocationsInput, props.context.actionContext);
};

const _getCustomAttribute = async (
    _allAttributes: AttributeValue[],
    selectedAttributes: string[] | undefined,
    stateAttributes: AttributeValue[],
    setAttributes: React.Dispatch<React.SetStateAction<AttributeValue[]>>,
    setIsAgeRestricted: React.Dispatch<React.SetStateAction<boolean>>,
    setIsVendorShipped: React.Dispatch<React.SetStateAction<boolean>>
) => {
    if (stateAttributes?.length > 0 || !selectedAttributes?.length) return;

    const customAttributes: AttributeValue[] = [];
    selectedAttributes.forEach(selectedAttribute => {
        const matchingAttribute = _allAttributes.find(
            attribute => selectedAttribute.trim().toLowerCase() === attribute.Name?.toLowerCase()
        );
        if (matchingAttribute) customAttributes.push(matchingAttribute);
    });

    setAttributes(customAttributes);

    const ageRestrictedAttribute = _allAttributes.find(attr => {
        const name = attr.Name?.trim().toLowerCase();
        return name === 'agerestriction' || name === 'age restriction';
    });
    if (ageRestrictedAttribute?.TextValue?.trim().toLowerCase() === 'true') {
        setIsAgeRestricted(true);
    }

    const isvendorShippedAttribute = _allAttributes.find(attr => attr.Name?.trim().toLowerCase() === 'isvendershipproduct');
    const isVendorShipTextValue = isvendorShippedAttribute?.TextValue?.trim().toLowerCase();
    if (!(isVendorShipTextValue === 'yes' || isVendorShipTextValue === 'true')) {
        setIsVendorShipped(false);
    }
};

const _renderAddedToCartNotification = (resources: IBuyboxResources, navigationUrl: string | undefined): JSX.Element => {
    const { buyBoxHeaderMessageText, buyBoxGoToCartText } = resources;
    return (
        <Node className='msc-added-to-cart-notification'>
            <Node className='msc-add-to-cart-notification__title'>{buyBoxHeaderMessageText}</Node>
            <Node className='msc-add-to-cart-notification__button'>
                <Msdyn365.Link
                    link={{ ariaLabel: buyBoxGoToCartText, linkText: buyBoxGoToCartText, linkUrl: navigationUrl, role: 'button' }}
                />
            </Node>
        </Node>
    );
};

const _renderReviewCount = (reviewsCount: number | undefined, resources: IBuyboxResources): JSX.Element => {
    const count = reviewsCount ?? 0;
    return (
        <Node tag='div' className='ms-buybox__reviews-count-section'>
            <Node tag='span' className='reviews-count'>
                {count}
            </Node>
            <Node tag='span' className='reviews-count-label'>
                {reviewsCount === 1 ? resources.reviewCountLabelForOne : resources.reviewCountLabelForMore}
            </Node>
        </Node>
    );
};

const _renderTextBlock = (textBlocks: React.ReactNode[]): JSX.Element | undefined => {
    return textBlocks?.length > 0 ? <>{textBlocks[0]}</> : undefined;
};

const _renderConfigure = (configure: IBuyboxProductConfigureViewProps): JSX.Element => {
    const { ContainerProps, dropdowns } = configure;
    return <Node {...ContainerProps}>{dropdowns.map(_renderConfigureDropdown)}</Node>;
};

const _renderConfigureDropdown = (dropdown: IBuyboxProductConfigureDropdownViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, errors, select } = dropdown;
    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>
                {heading}
                {errors}
            </Node>
            {select}
        </Node>
    );
};

const _renderFindInStore = (findInStore: IBuyboxFindInStoreViewProps): JSX.Element => {
    const { ContainerProps, storeSelector, errors, modal } = findInStore;
    return (
        <Node {...ContainerProps}>
            {storeSelector}
            {errors}
            {modal}
        </Node>
    );
};

const _renderSocialShare = (socialShare: React.ReactNode[] | undefined): JSX.Element | undefined => {
    return socialShare?.length && socialShare?.length > 0 ? <>{socialShare[0]}</> : undefined;
};

const _renderShopSimilarItem = (shopSimilarItem: IBuyboxShopSimilarLookViewProps): JSX.Element => {
    const { ContainerProps, errors, input } = shopSimilarItem;
    return (
        <Node {...ContainerProps}>
            {errors}
            {input}
        </Node>
    );
};

// Bundle helper functions
const _addAllProductsToCart = async (
    props: IBuyboxProps<IBuyboxData & IBuyboxViewData>,
    cartProducts: IDobbiesMiniCartSummaryProduct[]
): Promise<void> => {
    const cookies = props.context.actionContext.requestContext.cookies;
    const productsAddedInBundles = cookies.get('added_products_bundles')?.value || [];
    const productList: any[] = [];
    const allStores = await _getAllStores(props, 31.5070767, 74.4315609, 0, 0);

    const simpleProducts = cartProducts.filter(cp => {
        return Array.isArray(productsAddedInBundles) && productsAddedInBundles.find(addedp => addedp['productId'] === cp.RecordId);
    });

    simpleProducts.forEach(p => {
        if (!Array.isArray(productsAddedInBundles)) return;
        const ap = productsAddedInBundles.find(addedp => addedp['productId'] === p.RecordId);
        let quantity = ap['quantity'];
        const prodInCart = props.data.cart.result?.cart.CartLines?.find(cl => cl.ProductId === ap['productId']);
        if (prodInCart?.Quantity && quantity + prodInCart.Quantity > ap['maxQuantity']) {
            quantity = -1;
        }
        const inventoryLocationId = ap['inventoryLocationId'];
        if (inventoryLocationId) {
            const storeLocation = allStores.find(
                sl => sl.OrgUnitAvailability?.OrgUnitLocation?.InventoryLocationId === inventoryLocationId
            );
            if (storeLocation?.OrgUnitAvailability?.OrgUnitLocation) {
                productList.push({
                    product: p,
                    count: quantity,
                    location: storeLocation.OrgUnitAvailability.OrgUnitLocation,
                    deliveryMode: 'ClickCol'
                });
            }
        } else {
            productList.push({
                product: p,
                count: quantity,
                enableStockCheck: props.context.app.config.enableStockCheck
            });
        }
    });

    if (productList.some(sp => sp.count === -1)) return;

    props.data.cart.result
        ?.addProductsToCart(productList)
        .then(() => {
            MsDyn365.isBrowser && window?.location.assign('/cart');
            cookies.remove('added_products_bundles');
        })
        .catch(error => console.error('Error', error));
};

const _onQuantityChange = async (
    props: IBuyboxProps<IBuyboxData & IBuyboxViewData>,
    productId: number,
    newValue: number,
    cartProducts: IDobbiesMiniCartSummaryProduct[],
    setCartProducts: React.Dispatch<React.SetStateAction<IDobbiesMiniCartSummaryProduct[]>>,
    isSummaryInModal: boolean,
    maxQuantityForBundle: number
) => {
    const cookies = props.context.actionContext.requestContext.cookies;
    const addedProductsToCollection = cookies.get('added_products_bundles')?.value || [];
    const cartProductsCopy = [...cartProducts];
    const updatedProducts: IDobbiesAddedProductToBundle[] = [];

    if (Array.isArray(addedProductsToCollection)) {
        for (const obj of addedProductsToCollection) {
            if (obj['productId'] === productId) {
                if (newValue > 0) {
                    updatedProducts.push({
                        productId: obj['productId'],
                        price: obj['price'],
                        quantity: newValue,
                        maxQuantity: obj['maxQuantity'],
                        inventoryLocationId: obj['inventoryLocationId']
                    });
                    const cp = cartProductsCopy.find(c => c.RecordId === obj['productId']);
                    if (cp) cp.quantity = newValue;
                }
            } else {
                updatedProducts.push({
                    productId: obj['productId'],
                    price: obj['price'],
                    quantity: obj['quantity'],
                    maxQuantity: obj['maxQuantity'],
                    inventoryLocationId: obj['inventoryLocationId']
                });
            }
        }
        cookies.set('added_products_bundles', updatedProducts);
        setCartProducts(cartProductsCopy);
        props.context.actionContext.update(new CartModulesStateShareInput(), { isQuantityUpdated: true });
    }
};

const _onRemoveProduct = (props: IBuyboxProps<IBuyboxData & IBuyboxViewData>, productId: number, isSummaryInModal: boolean) => {
    const cookies = props.context.actionContext.requestContext.cookies;
    let productsAddedInBundles = cookies.get('added_products_bundles')?.value || [];

    if (!isSummaryInModal && Array.isArray(productsAddedInBundles)) {
        const updatedProductsAddedInBundles = productsAddedInBundles
            .filter(prod => prod && prod['productId'] !== productId)
            .map(prod => ({
                productId: prod['productId'],
                quantity: prod['quantity'],
                price: prod['price'],
                maxQuantity: prod['maxQuantity']
            }));
        cookies.set('added_products_bundles', updatedProductsAddedInBundles);
        props.context.actionContext.update(new CartModulesStateShareInput(), { isCartUpdated: true });
    }
};

export default BuyboxView;
